import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

interface TruckSession {
  token: string;
  truck_id: string;
  truck_name: string;
  phone_number: string;
  expires_at: number;
}

interface TruckSessionContextType {
  session: TruckSession | null;
  isAuthenticated: boolean;
  login: (session: TruckSession) => void;
  logout: () => void;
}

const TruckSessionContext = createContext<TruckSessionContextType | undefined>(undefined);

export function TruckSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<TruckSession | null>(null);
  const navigate = useNavigate();

  // Load session from localStorage on mount
  useEffect(() => {
    const storedSession = localStorage.getItem('truck_session');
    if (storedSession) {
      try {
        const parsed = JSON.parse(storedSession);

        // Check if session is expired
        const now = Math.floor(Date.now() / 1000);
        if (parsed.expires_at > now) {
          setSession(parsed);
        } else {
          // Session expired, clear it
          localStorage.removeItem('truck_session');
        }
      } catch (error) {
        logger.error('Failed to parse session:', error);
        localStorage.removeItem('truck_session');
      }
    }
  }, []);

  // Auto-logout on token expiry
  useEffect(() => {
    if (!session) return;

    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = (session.expires_at - now) * 1000; // Convert to milliseconds

    if (timeUntilExpiry <= 0) {
      logout();
      return;
    }

    // Set timeout to logout when token expires
    const timer = setTimeout(() => {
      logout();
    }, timeUntilExpiry);

    return () => clearTimeout(timer);
  }, [session]);

  const login = (newSession: TruckSession) => {
    setSession(newSession);
    localStorage.setItem('truck_session', JSON.stringify(newSession));
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem('truck_session');
    navigate('/truck-login');
  };

  return (
    <TruckSessionContext.Provider
      value={{
        session,
        isAuthenticated: !!session,
        login,
        logout,
      }}
    >
      {children}
    </TruckSessionContext.Provider>
  );
}

export function useTruckSession() {
  const context = useContext(TruckSessionContext);
  if (context === undefined) {
    throw new Error('useTruckSession must be used within a TruckSessionProvider');
  }
  return context;
}
